import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Eyebrow } from "./SectionShell";

const CATEGORIES = [
  { title: "Protect", items: ["Term & Whole Life Insurance", "Critical Illness & Disability", "Mortgage & Income Protection"] },
  { title: "Recover", items: ["Money Back Life™", "Limited-pay Permanent", "Return-of-Premium Options"] },
  { title: "Prosper", items: ["Prosperity Life™", "Tax-Free Retirement Income", "Cash-Value Wealth Accumulation"] },
  { title: "Business Owners", items: ["Corporate-Owned Life Insurance", "Buy-Sell & Key-Person Funding", "IPP / RCA Planning"] },
  { title: "Family", items: ["Junior Wealth Builder™", "RESP + CESG Optimisation", "Estate & Legacy Design"] },
  { title: "Advisory", items: ["Second Opinions", "Annual Confidence Reviews", "Coordinated Tax & Legal Referrals"] },
];

const EASE = [0.32, 0.72, 0, 1];
const listVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };
const rowVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const slug = (t) => t.toLowerCase().replace(/\s+/g, "-");

function SolutionRow({ cat, index }) {
  return (
    <motion.li
      variants={rowVariants}
      className="group relative grid grid-cols-[3rem_1fr] md:grid-cols-12 gap-x-4 md:gap-x-8 gap-y-3 py-8 md:py-10 border-b border-[#E6E2D8]"
      data-testid={`solution-${slug(cat.title)}`}
    >
      <span className="pointer-events-none absolute left-0 bottom-[-1px] h-px w-full origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 bg-[#B08D57]" />
      <div className="md:col-span-1 pt-2 md:pt-3 font-display italic text-lg text-[#B08D57] tabular-nums">
        {String(index + 1).padStart(2, "0")}
      </div>
      <h3 className="md:col-span-4 font-display text-[#0B1F3F] text-[28px] md:text-[36px] leading-[1.1] tracking-tight transition-transform duration-500 group-hover:translate-x-2">
        {cat.title}
      </h3>
      <ul className="col-start-2 md:col-start-auto md:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-y-2 sm:gap-x-6 md:pt-4">
        {cat.items.map((it) => (
          <li
            key={it}
            className="text-[#5A6476] text-[15px] leading-snug transition-colors duration-300 group-hover:text-[#0B1F3F] sm:border-l sm:border-[#E6E2D8] sm:pl-4"
          >
            {it}
          </li>
        ))}
      </ul>
    </motion.li>
  );
}

export default function SolutionsGrid() {
  return (
    <section
      id="services"
      className="relative w-full py-20 md:py-32 bg-white scroll-mt-20"
      data-testid="solutions-grid"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-end mb-12 md:mb-16">
          <div className="lg:col-span-7">
            <Eyebrow>Solutions</Eyebrow>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0B1F3F] mt-4 leading-[1.05]"
              data-testid="solutions-title"
            >
              Built <span className="italic text-[#8A6A2E]">for you</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-base md:text-lg text-[#5A6476] leading-relaxed">
            Six coordinated disciplines, from the first term policy through the
            corporately-owned estate bond. Not a menu of products, but a single
            strategy across your life.
          </p>
        </div>

        <motion.ol
          className="border-t border-[#E6E2D8]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={listVariants}
          data-testid="solutions-list"
        >
          {CATEGORIES.map((cat, i) => (
            <SolutionRow key={cat.title} cat={cat} index={i} />
          ))}
        </motion.ol>

        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <Link to="/assessment" className="btn-primary" data-testid="solutions-cta">
            Find your fit in 3 minutes
          </Link>
          <p className="text-[#5A6476] text-sm max-w-sm">
            The Confidence Scorecard maps you to the right stage automatically.
          </p>
        </div>
      </div>
    </section>
  );
}
